/* eslint-disable react-hooks/rules-of-hooks */
import cx from "classnames";
import { FormikConfig, FormikErrors, FormikTouched, useFormik } from "formik";
import get from 'lodash/get';
import React, {
  ButtonHTMLAttributes,
  HTMLAttributes,
  InputHTMLAttributes,
  LabelHTMLAttributes,
  SelectHTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
// @ts-ignore
import { Action, Store, createStore } from "redux";

import { YupValidation } from "../entities/shared/YupValidation";
import { camelCaseToSentence } from "../utils/camelCaseToSentence";
import { NestedSubType } from "../utils/NestedKeys";
import { PrimitiveSubType } from "../utils/PrimitiveKeys";

import "./typedForm.css";
import { CircleAlertFill, CircleCheckFill } from "akar-icons";

interface TypedInputProps<T extends object, V extends keyof PrimitiveSubType<T> & string>
  extends InputHTMLAttributes<HTMLInputElement> {
  type: PrimitiveSubType<T>[V] extends boolean
    ? "checkbox"
    : PrimitiveSubType<T>[V] extends string
      ? "text" | "password" | "email" | "tel" | "radio"
      : PrimitiveSubType<T>[V] extends number
        ? "number" | "radio"
        : never;
  radioId?: PrimitiveSubType<T>[V] extends string ? PrimitiveSubType<T>[V] : never;
  id?: never;
  name?: never;
  onChange?: never;
  value?: never;
  defaultValue?: never;
  form?: never;
}

interface ISelectOption<T> {
  value: T;
  label?: string;
  disabled?: boolean;
}

interface TypedDropdownProps<T extends object, V extends keyof PrimitiveSubType<T>>
  extends SelectHTMLAttributes<HTMLSelectElement> {
  options: ISelectOption<PrimitiveSubType<T>[V]>[];
  form?: never;
  onChange?: never;
  value?: never;
  defaultValue?: never;
}

interface TypedLabelProps<T extends object, V extends keyof PrimitiveSubType<T> & string>
  extends LabelHTMLAttributes<HTMLLabelElement> {
  htmlFor?: never;
  radioId?: PrimitiveSubType<T>[V] extends string ? PrimitiveSubType<T>[V] : never;
}

// eslint-disable-next-line  @typescript-eslint/no-unused-vars
interface ValidationErrorProps<T extends object, V extends keyof T & string>
  extends HTMLAttributes<HTMLDivElement> {
  hideIfFormClean?: boolean;
  useIcons?: boolean;
  children?: never;
}

interface FormConfigWithTypedValidationScheme<T extends object> extends FormikConfig<T> {
  validationSchema: YupValidation<T>;
  validate?: never;
  component?: never;
  render?: never;
  children?: never;
  innerRef?: never;
}

interface FormikStoreValues<T> {
  values: T & object;
  errors: FormikErrors<T> & object;
  touched: FormikTouched<T> & object;
}
const pseudoActionType = {}; // action type is required, just making random pointer to fulfill that requirement
interface FormikStoreAction<T> extends Action<typeof pseudoActionType>, FormikStoreValues<T> {}

export function useTypedForm<T extends object>(formikConfig: FormConfigWithTypedValidationScheme<T>) {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    handleReset,
    setFieldValue,
    validateForm,
  } = useFormik<T>(formikConfig);

  // store needed to share state with fields
  const store: Store<FormikStoreValues<T>, FormikStoreAction<T>> = useMemo(() => {
    const initialValues: FormikStoreValues<T> = {
      values,
      errors,
      touched,
    };

    return createStore<FormikStoreValues<T>, FormikStoreAction<T>, never, never>(
      (
        __,
        {
          values: currentValues = initialValues.values,
          errors: currentErrors = initialValues.errors,
          touched: currentTouched = initialValues.touched,
        }
      ) => ({
        values: currentValues,
        errors: currentErrors,
        touched: currentTouched,
      }),
      initialValues as FormikStoreValues<T> as any // can't deal with odd type behavior around redux's "PreloadedState" type right now
    );
  }, []);

  // keep store in sync with form values
  useEffect(() => {
    store.dispatch({
      type: pseudoActionType,
      values,
      errors,
      touched,
    });
  }, [values, errors, touched]);

  const createUseField = useGenerateCreateUseField(store, handleBlur, handleChange);

  // ideally might move this to separate function, not worth it right now
  const useSubForm = useCallback(
    <ContainerT extends object, V extends keyof NestedSubType<ContainerT> & string>(nestedField: V) => {
      type ContainerNestedSubType = NestedSubType<ContainerT>;
      type NestedPrimitives = PrimitiveSubType<ContainerNestedSubType[V]>;
      type NestedNested = NestedSubType<ContainerNestedSubType[V]>;

      const useNestedField = createUseField<NestedPrimitives>(nestedField);
      const useNestedSubForm = <
        NextNested extends NestedNested,
        NextNestedKey extends keyof NestedSubType<NextNested> & string,
      >(
        nextNested: NextNestedKey
      ) => {
        return useSubForm<NextNested, NextNestedKey>(
          // cheating here to accumulate string because typed nested key paths are impossible
          `${nestedField}.${nextNested}` as any
        );
      };

      const result: [typeof useNestedField, typeof useNestedSubForm] = [useNestedField, useNestedSubForm];

      return result;
    },
    [createUseField]
  );

  const baseUseSubForm = useCallback(
    <V extends keyof NestedSubType<T> & string>(nestedField: V) => useSubForm<T, V>(nestedField),
    [useSubForm]
  );

  const useValues = useCallback(() => {
    const [currentValues, setCurrentValues] = useState(values);
    useEffect(() => {
      let timeout: NodeJS.Timeout;
      // subscribing to redux returns unsubscribing function
      const unsubscribe = store.subscribe(() => {
        timeout = setTimeout(() => {
          clearTimeout(timeout);
          const storeValues = store.getState().values;
          if (currentValues !== storeValues) {
            setCurrentValues(storeValues);
          }
        }, 0);
      });

      return () => {
        clearTimeout(timeout);
        unsubscribe();
      };
    }, []);

    return currentValues;
  }, []);

  return useMemo(
    () => ({
      useValues,
      setFieldValue,
      validateForm,
      useField: createUseField<T>(""),
      useSubForm: baseUseSubForm,
      Submit: React.memo(({ children = "Submit", ...props }: ButtonHTMLAttributes<HTMLButtonElement>) => {
        // event needs to be casted to "any", don't want to enforce wrapping with "form"
        const _handleSubmit = useCallback((e: any) => handleSubmit(e), []);

        return (
          <button type="submit" {...props} onClick={_handleSubmit}>
            {children}
          </button>
        );
      }),
      Reset: React.memo(({ children = "Reset", ...props }: ButtonHTMLAttributes<HTMLButtonElement>) => (
        <button type="reset" {...props} onClick={handleReset}>
          {children}
        </button>
      )),
    }),
    []
  );
}

// opportunity for better optimization later
export function useGenerateCreateUseField<T extends object>(
  store: Store<FormikStoreValues<T>, FormikStoreAction<T>>,
  handleBlur: (e: React.FocusEvent<any>) => void, // not dealing with "any" here
  handleChange: (e: React.ChangeEvent<any>) => void // not dealing with "any" here
) {
  return useCallback(
    <SubT extends { [key: string]: any }>(
      prefixKeyPath: string // cheating with string type for "prefixKeyPath" as nested keypath can't be typed
    ) =>
      <V extends keyof PrimitiveSubType<SubT> & string>(field: V) =>
        useMemo((): {
          Input: React.MemoExoticComponent<(inputProps: TypedInputProps<SubT, V>) => JSX.Element>;
          Label: React.MemoExoticComponent<(labelProps: TypedLabelProps<SubT, V>) => JSX.Element>;
          ValidationError: React.MemoExoticComponent<
            (validationProps: ValidationErrorProps<SubT, V>) => JSX.Element | null
          >;
          Dropdown: React.MemoExoticComponent<(validationProps: TypedDropdownProps<SubT, V>) => JSX.Element>;
        } => {
          const combinedKeyPath = [prefixKeyPath, field].filter(Boolean).join(".");

          // cheating with untyped because subpaths are actually impossible :(
          const getValue: () => SubT[V] = () => get(store.getState().values, combinedKeyPath);
          const getTouched: () => boolean = () => get(store.getState().touched, combinedKeyPath);
          const getError: () => FormikErrors<SubT>[V] = () => get(store.getState().errors, combinedKeyPath);

          const sentenceCasedField = camelCaseToSentence(field);

          const Input = ({
            radioId,
            placeholder,
            className,
            type: inputType,
            ...inputProps
          }: TypedInputProps<SubT, V>) => {
            const [value, setValue] = useState<SubT[V]>(getValue());
            const [hasError, setHasError] = useState<boolean>(!!getError());
            const [isDirty, setIsDirty] = useState<boolean>(!!getTouched());

            const finalValue =
              value || // including fallbacks to avoid console warning
              (inputType === "checkbox" ? false : inputType === "number" ? 0 : "");

            const finalPlaceholder = useMemo(
              () => (inputType === "checkbox" ? undefined : placeholder || sentenceCasedField),
              [placeholder]
            );

            useEffect(
              () =>
                // subscribing to redux returns unsubscribing function
                store.subscribe(() => setValue(getValue())),
              []
            );
            useEffect(
              () =>
                // subscribing to redux returns unsubscribing function
                store.subscribe(() => setHasError(!!getError())),
              []
            );
            useEffect(
              () =>
                // subscribing to redux returns unsubscribing function
                store.subscribe(() => setIsDirty(!!getTouched())),
              []
            );

            return (
              <input
                id={radioId || combinedKeyPath}
                name={combinedKeyPath}
                placeholder={finalPlaceholder}
                type={inputType}
                onChange={handleChange}
                onBlur={handleBlur}
                className={cx(className, "form-input", {
                  "form-error": hasError,
                  "form-dirty": isDirty,
                  "form-clean": !isDirty,
                })}
                // TODO: figure out how to not use "any" here
                defaultValue={inputType === "checkbox" ? (undefined as any) : finalValue}
                checked={inputType === "checkbox" ? finalValue : (undefined as any)}
                {...inputProps}
              />
            );
          };

          const Label = ({ children, radioId, className, ...labelProps }: TypedLabelProps<SubT, V>) => {
            const [hasError, setHasError] = useState<boolean>(!!getError());
            const [isDirty, setIsDirty] = useState<boolean>(!!getTouched());

            useEffect(
              () =>
                // subscribing to redux returns unsubscribing function
                store.subscribe(() => setHasError(!!getError())),
              []
            );
            useEffect(
              () =>
                // subscribing to redux returns unsubscribing function
                store.subscribe(() => setIsDirty(!!getTouched())),
              []
            );

            return (
              <label
                htmlFor={radioId || combinedKeyPath}
                className={cx(className, "form-input-label", {
                  "form-error": hasError,
                  "form-dirty": isDirty,
                  "form-clean": !isDirty,
                })}
                {...labelProps}
              >
                {children}
              </label>
            );
          };

          const ValidationError = ({
            hideIfFormClean,
            className,
            useIcons,
            ...otherProps
          }: ValidationErrorProps<SubT, V>) => {
            const [error, setError] = useState<FormikErrors<SubT>[V]>(getError());
            const [isDirty, setIsDirty] = useState<boolean>(!!getTouched());
            useEffect(
              () =>
                // subscribing to redux returns unsubscribing function
                store.subscribe(() => setIsDirty(!!getTouched())),
              []
            );

            useEffect(
              () =>
                // subscribing to redux returns unsubscribing function
                store.subscribe(() => {
                  const errorFromState = getError();
                  setError(
                    typeof errorFromState === "string"
                      ? (replaceAll(errorFromState, field, sentenceCasedField) as any)
                      : errorFromState
                  );
                }),
              []
            );

            return hideIfFormClean && !isDirty ? null : (
              <div
                {...otherProps}
                className={cx(className, "form-validation-error", {
                  "form-dirty": isDirty,
                  "form-clean": !isDirty,
                })}
              >
                {useIcons ? (
                  <React.Fragment>
                    {error ? (
                      <CircleAlertFill color="#C61E00" size={18} />
                    ) : (
                      <CircleCheckFill color="#D1E7C3" size={18} />
                    )}
                  </React.Fragment>
                ) : (
                  <React.Fragment>{typeof error === "string" ? error : ""}</React.Fragment>
                )}
              </div>
            );
          };

          const Dropdown = ({ options, className, ...props }: TypedDropdownProps<SubT, V>) => {
            const [value, setValue] = useState<SubT[V]>(getValue());
            const [hasError, setHasError] = useState<boolean>(!!getError());
            const [isDirty, setIsDirty] = useState<boolean>(!!getTouched());

            const onChangeWrapped = useCallback(
              (newValue: string) =>
                handleChange({
                  target: { name: combinedKeyPath, value: newValue },
                  currentTarget: { name: combinedKeyPath, value: newValue },
                } as React.ChangeEvent<HTMLInputElement>),
              [handleChange, name]
            );

            const onBlurWrapped = useCallback(
              () =>
                handleBlur({
                  target: { name: combinedKeyPath, value: value as any },
                  currentTarget: { name: combinedKeyPath, value: value as any },
                } as React.FocusEvent<HTMLInputElement>),
              [handleBlur, name]
            );

            useEffect(
              () =>
                // subscribing to redux returns unsubscribing function
                store.subscribe(() => setValue(getValue())),
              []
            );
            useEffect(
              () =>
                // subscribing to redux returns unsubscribing function
                store.subscribe(() => setHasError(!!getError())),
              []
            );
            useEffect(
              () =>
                // subscribing to redux returns unsubscribing function
                store.subscribe(() => setIsDirty(!!getTouched())),
              []
            );

            return (
              <select
                {...props}
                className={cx(className, "form-select", {
                  "form-error": hasError,
                  "form-dirty": isDirty,
                  "form-clean": !isDirty,
                })}
                value={value}
                onChange={e => onChangeWrapped(e.target.value)}
                onBlur={onBlurWrapped}
              >
                {options.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label || o.value}
                  </option>
                ))}
              </select>
            );
          };

          return {
            Input: React.memo(Input),
            Label: React.memo(Label),
            ValidationError: React.memo(ValidationError),
            Dropdown: React.memo(Dropdown),
          };
        }, [field]),
    [store, handleChange, handleBlur]
  );
}

function replaceAll(str: string, toReplace: string, replaceWith: string) {
  // If a regex pattern
  if (Object.prototype.toString.call(str).toLowerCase() === "[object regexp]") {
    return str.replace(toReplace, replaceWith);
  }

  // If a string
  return str.replaceAll(new RegExp(toReplace, "g"), replaceWith);
}
